import { request, type HttpWithRetryOptions } from '../request';
import { namespace, env, version } from '../globals';
import { uuid, timestamp } from '@roai/shared';

/**
 * 发送崩溃报告到服务器
 * @param crashData 崩溃数据
 * @param options 请求选项
 * @returns Promise<CrashReportResponse>
 */
export async function reportCrash(
  crashData: {
    customData: { _$: string; } & Record<string, unknown>;
    stackTrace: string;
    message?: string;
  },
  options?: Partial<HttpWithRetryOptions>
): Promise<unknown> {
  const now = new Date();
  const reportId = `web_${env}_${uuid()}`;
  const url = env === 'production' ? 'https://crash-server.brainx.tech/sdk/crash/report' : 'https://test01-as-ssp-sdk-server.tec-develop.cn/sdk/crash/report';

  const { customData, stackTrace, message } = crashData;

  const data = {
    // 自动生成的字段
    reportId,
    sdkVersion: parseInt(version.replace(/\./g, '')),
    appVersionCode: 1,
    appVersionName: '1.0',
    packageName: namespace,
    appName: location.hostname,
    
    // 从设备信息获取的字段
    phoneModel: navigator.platform,
    buildLevel: 1,
    direction: getScreenOrientation(),
    buildVersion: '1.0',
    brand: '',
    product: '',
    network: getNetworkType(),
    userAgent: navigator.userAgent,
    display: `${window.screen.width}*${window.screen.height}`,
    faid: '',
    
    // 内存相关字段
    totalMemSize: 1,
    availableMemSize: 1,
    dumpsysMemInfo: '',
    stackTraceHash: message?.slice(0, 60),
    threadDetails: location.href,
    
    // 时间相关字段
    userAppStartDate: '',
    userCrashDate: getChinaTimeString(now),
    customData: JSON.stringify(customData),
    stackTrace, 
  }
  
  const defaultOptions: HttpWithRetryOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
    timeout: 10000, // 10秒超时
  };

  try {
    const response = await request.send(url, {
      ...defaultOptions,
      ...options
    });

    return {
      success: response.data.status >= 200 && response.data.status < 300,
      message: response.data.statusText,
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : '未知错误'
    };
  }
}
/**
 * 获取屏幕方向
 * @returns 屏幕方向 (1: 竖屏, 2: 横屏)
 */
function getScreenOrientation(): number {
  return window.innerHeight > window.innerWidth ? 1 : 2;
}

/**
 * 获取网络类型
 * @returns
 */
function getNetworkType(): number {
  if ('connection' in navigator) {
    const connection = navigator.connection;
    if (connection) {
      switch (connection.effectiveType) {
        case '4g':
          return 4;
        case '3g':
          return 3;
        case '2g':
          return 2; // 移动网络
        case 'slow-2g':
          return 1; // 其他
        default:
          return 0; // WiFi
      }
    }
  }
  return 0;
}

/**
 * 获取中国时区的时间字符串
 * @param date Date对象
 * @returns 中国时区的ISO格式时间字符串
 */
function getChinaTimeString(date: Date): string {
  // 获取当前时间的UTC时间戳
  const utcTime = date.getTime();
  // 中国时区偏移量：UTC+8小时
  const chinaOffset = 8 * 60 * 60 * 1000; // 8小时转换为毫秒
  // 计算中国时区的时间戳
  const chinaTime = new Date(utcTime + chinaOffset);
  return chinaTime.toISOString();
}

export async function reportError(
  obj: {
    bizType: string;
    logType: string;
    logName: string;
    params: Record<string, unknown>;
  }
): Promise<unknown> {
  const origin = window.__n_host__ ? window.__n_host__ : 'https://tracking.roaitech.ai';
  const url = env === 'production' ? origin + '/sdk/log/report' : 'https://test01-sdk-tracking-server.tec-develop.cn/sdk/log/report';

  const { bizType, logName, params, logType } = obj;

  const data = {
    bizType,
    platform: 'web',
    sdkVersion: version,
    appId: location.host ? location.host : 'no host app',
    appVersion: '1.0',
    device:  navigator.platform,
    os: navigator.platform,
    network: getNetworkType(),
    display: `${window.screen.width}*${window.screen.height}`,
    userAgent: navigator.userAgent,
    page: location.href,
    timestamp: timestamp(),
    date: getChinaTimeString(new Date()),
    logType: logType || 'error',
    logName,
    params,
  }
  
  const defaultOptions: HttpWithRetryOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
    timeout: 10000, // 10秒超时
  };

  try {
    if (navigator && typeof navigator === 'object' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([JSON.stringify(data)]);
      const res = navigator.sendBeacon(url, blob);
      return {
        success: res,
      }
    } else {
      const response = await request.send(url, {
        ...defaultOptions,
      });
      return {
        success: response.data.status >= 200 && response.data.status < 300,
        message: response.data.statusText,
      };
    }
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : '未知错误'
    };
  }
}
